import type { BaseClubDto } from '@/core/data/club/club.interface';
import type { ClaimedHandshakeDto, HandshakeDto, LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { StandInVerdict } from '@/core/data/handshake/handshake.type';
import type { CityDto, GeoCoords, UserGeoData, UserLocationData } from '@/core/data/location/location.interface';
import type {
  BaseUserDto,
  BaseUserInfo,
  GetUsersOptions,
  UserDto,
  ValidateUserDto,
} from '@/core/data/user/user.interface';
import { UserRepository } from '@/core/data/user/user.repository';
import type { PlayerType } from '@/core/data/user/user.type';
import FormHelper from '@/core/helpers/form.helper';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { NotificationTopics } from '@/views/composables/constants/gam.constants';
import type { ProfileImagesForm, ProfileUserForm } from '@/views/composables/models/form.interface';

export class UserService {
  private readonly repository: UserRepository;

  constructor(repository: UserRepository) {
    this.repository = repository;
  }

  async getLocationFromCountry(): Promise<Result<UserLocationData>> {
    try {
      const response = await this.repository.getLocationFromCountry();

      if (!response?.data?.latitude || !response?.data?.longitude) return { error: generalError() };

      return { data: response.data };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getIpLocation(): Promise<Result<UserLocationData>> {
    try {
      const response = await this.repository.getIpLocation();

      if (response === null) {
        return { error: generalError() };
      }

      return { data: response.data };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateLocation(geoData: UserGeoData): Promise<Result<UserGeoData>> {
    try {
      const response = await this.repository.updateLocation(geoData);

      if (!response?.data?.updatedAt) return { error: generalError() };

      return { data: response.data };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateCity(coordinates: GeoCoords): Promise<Result<CityDto>> {
    try {
      const response = await this.repository.updateCity(coordinates);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getUsers(listId: GamListId, options?: GetUsersOptions): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getUsers(listId, options);
      if (!response?.data) return { error: generalError() };

      return {
        data: response.data.filter((u) => !!u.username && !!u.rating),
        cursor: response.cursor,
      };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getFollowersByQuery(query: string): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getFollowersByQuery(query);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getFollowers(): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getFollowers();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getFollowing(): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getFollowing();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getUser(id: string): Promise<Result<UserDto>> {
    try {
      const response = await this.repository.getUser(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getUserByName(username: string): Promise<Result<UserDto>> {
    try {
      const response = await this.repository.getUserByName(username);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getWorldHandshakePairs(userId: string): Promise<Result<{ worldUniqueHandshakePairs: number }>> {
    try {
      const response = await this.repository.getWorldHandshakePairs(userId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getHandshakePartners(userId: string): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getHandshakePartners(userId);
      if (!response?.data?.handshakes) return { data: [] };

      const seen = new Set<string>();
      const partners: BaseUserDto[] = [];

      for (const h of response.data.handshakes) {
        if (!h.isConfirmed) continue;
        const partner = h.initiatorId === userId ? h.responder : h.initiator;
        if (!partner || seen.has(partner.id)) continue;
        seen.add(partner.id);
        partners.push({
          id: partner.id,
          username: partner.username,
          firstName: partner.firstName,
          lastName: partner.lastName,
          rating: partner.rating,
          profileImageBlobHash: partner.profileImageBlobHash,
        } as BaseUserDto);
      }

      return { data: partners };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getRecentInvitees(): Promise<Result<BaseUserDto[]>> {
    try {
      const response = await this.repository.getRecentInvitees();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getUserInfo(refresh?: boolean): Promise<Result<BaseUserInfo>> {
    try {
      const response = await this.repository.getUserInfo(refresh);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getUserClubs(refresh?: boolean): Promise<Result<BaseClubDto[]>> {
    try {
      const response = await this.repository.getUserClubs(refresh);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateUser(profileData: ProfileUserForm): Promise<Result<boolean>> {
    try {
      const response = await this.repository.updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        username: profileData.username,
        emoji: profileData.emoji || undefined,
        aboutMe: profileData.aboutMe || undefined,
        birthDate: profileData.birthDate ? new Date(profileData.birthDate).toISOString() : profileData.birthDate,
        profileImage: FormHelper.parseImageFile(profileData.profileImage),
        chessboardImage: FormHelper.parseImageFile(profileData.chessboardImage),
        playerTypes: profileData.playerTypes?.join(',') || undefined,
      });
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateNotificationPreferences(
    notificationPreferences: import('@/core/data/user/user.interface').NotificationPreferences,
  ): Promise<Result<import('@/core/data/user/user.interface').NotificationPreferences | undefined>> {
    try {
      const response = await this.repository.updateNotificationPreferences(notificationPreferences);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateUnitTypePreferences(unitType: string): Promise<Result<string>> {
    try {
      const response = await this.repository.updateUnitTypePreferences(unitType);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async markActive(): Promise<Result<boolean>> {
    try {
      const response = await this.repository.markActive();
      return response?.data ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateAccentHue(accentHue: number | null): Promise<Result<any>> {
    try {
      const response = await this.repository.updateAccentHue(accentHue);
      return response?.data !== undefined ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateRecentClockControls(
    controls: { minutes: number; increment: number }[],
  ): Promise<Result<{ minutes: number; increment: number }[]>> {
    try {
      const response = await this.repository.updateRecentClockControls(controls);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateEmoji(emoji: string): Promise<Result<any>> {
    try {
      const response = await this.repository.updateEmoji(emoji);
      return response?.data !== undefined ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updatePlayerTypes(
    profileData: ProfileUserForm,
    playerTypes: PlayerType[],
    birthDate: string,
    profileImage?: string,
  ): Promise<Result<boolean>> {
    try {
      const response = await this.repository.updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        username: profileData.username,
        emoji: profileData.emoji || undefined,
        aboutMe: profileData.aboutMe || undefined,
        birthDate: birthDate,
        profileImage: profileImage,
        playerTypes: playerTypes.join(','),
      });
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateImages(profileData: ProfileImagesForm): Promise<Result<boolean>> {
    try {
      const response = await this.repository.updateImages({
        profileImage: FormHelper.parseImageFile(profileData.profileImage),
        chessboardImage: FormHelper.parseImageFile(profileData.chessboardImage),
      });
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getUsername(username: string): Promise<Result<any>> {
    try {
      const response = await this.repository.getUsername(username);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async validateUsername(username: string): Promise<Result<ValidateUserDto>> {
    try {
      const response = await this.repository.validateUsername(username);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async storeNotificationToken(token: string, topics: NotificationTopics[] = []): Promise<Result<boolean>> {
    try {
      const response = await this.repository.storeNotificationToken(token, topics);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deactivateNotificationToken(token: string): Promise<Result<boolean>> {
    try {
      const response = await this.repository.deactivateNotificationToken(token);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deactivateAllNotificationTokens(): Promise<Result<boolean>> {
    try {
      const response = await this.repository.deactivateAllNotificationTokens();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async toggleFollow(id: string, isFollowing?: boolean): Promise<Result<boolean>> {
    try {
      const response = await this.repository.toggleFollow(id, isFollowing);
      return response ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async toggleBlock(id: string, isBlocked?: boolean): Promise<Result<boolean>> {
    try {
      const response = await this.repository.toggleBlock(id, isBlocked);
      return response ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async generateHandshakeCode(): Promise<Result<{ code: string; expiresAt: string }>> {
    try {
      const response = await this.repository.generateHandshakeCode();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async resolveHandshakeCode(code: string): Promise<Result<{ userId: string; username: string; token: string }>> {
    try {
      const response = await this.repository.resolveHandshakeCode(code);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async offerHandshake(
    playerId: string,
    image: File | null | string | undefined,
    options: {
      gambitId?: string;
      locationId?: string;
      token?: string;
      note?: string;
      suggestionSeed?: string;
      suggestionRound?: number;
    },
  ): Promise<Result<HandshakeDto> & { status?: number }> {
    try {
      const response = await this.repository.offerHandshake(playerId, image, options);
      return response?.data ? { data: response.data, status: response.status } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createPasserbyHandshake(
    options: { suggestionSeed?: string; suggestionRound?: number } = {},
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.createPasserbyHandshake(options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async claimPasserbyHandshake(code: string): Promise<Result<ClaimedHandshakeDto> & { status?: number }> {
    try {
      const response = await this.repository.claimPasserbyHandshake(code);
      return response?.data ? { data: response.data, status: response.status } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async acceptHandshake(
    handshakeId: string,
    image?: File | null | string | undefined,
    note?: string,
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.acceptHandshake(handshakeId, image, note);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLiveHandshakes(): Promise<Result<LiveHandshakeDto[]>> {
    try {
      const response = await this.repository.getLiveHandshakes();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async closeHandshake(handshakeId: string): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.closeHandshake(handshakeId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteHandshake(handshakeId: string): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.deleteHandshake(handshakeId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async addHandshakeImage(
    handshakeId: string,
    image: File,
    signal?: AbortSignal,
    positionIndex?: number,
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.addHandshakeImage(handshakeId, image, signal, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async moveHandshakeImage(
    handshakeId: string,
    imageId: string,
    positionIndex: number | null,
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.moveHandshakeImage(handshakeId, imageId, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async removeHandshakeImage(handshakeId: string, imageId: string): Promise<Result<void>> {
    try {
      const response = await this.repository.removeHandshakeImage(handshakeId, imageId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  /** The position to show before a coplayer sits: one this player has not checked. */
  async dealHandshakePosition(seed: string, round: number): Promise<Result<{ positionIndex: number }>> {
    try {
      const response = await this.repository.dealHandshakePosition(seed, round);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** The positions drawn and checked on a handshake, for the recommendation that shows them. */
  async getHandshakePositions(handshakeId: string): Promise<
    Result<{
      positions: number[];
      openPositions?: number[];
      declinedPositions?: number[];
      suggestionPositions: number[] | null;
    }>
  > {
    try {
      const response = await this.repository.getHandshakePositions(handshakeId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** No to Have you played. One answer settles the question for both players. */
  async declineHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<Result<{ declinedPositions: number[] }>> {
    try {
      const response = await this.repository.declineHandshakePosition(handshakeId, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** Play with the box on. Both phones can show the position before anyone checks it. */
  async startHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<Result<{ startedPositions: number[] }>> {
    try {
      const response = await this.repository.startHandshakePosition(handshakeId, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** Marks the position on the recommendation as played. One of the two checking is enough. */
  async checkHandshakePosition(handshakeId: string, positionIndex: number): Promise<Result<{ positions: number[] }>> {
    try {
      const response = await this.repository.checkHandshakePosition(handshakeId, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** Takes a checked position off, while the recommendation is still open. */
  async uncheckHandshakePosition(handshakeId: string, positionIndex: number): Promise<Result<{ positions: number[] }>> {
    try {
      const response = await this.repository.uncheckHandshakePosition(handshakeId, positionIndex);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** Swap colours, or keep a clock, on the recommendation both phones share. */
  async updateHandshakeSuggestion(
    handshakeId: string,
    change: { swapColours?: boolean; minutes?: number; increment?: number },
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.updateHandshakeSuggestion(handshakeId, change);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** Another recommendation off this meeting, the same one on both phones. */
  async nextHandshakeSuggestion(
    handshakeId: string,
    options: { keepColours?: boolean } = {},
  ): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.nextHandshakeSuggestion(handshakeId, options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** The handshakes on a profile, which is where a clock learns a new round. */
  async getHandshakes(userId: string): Promise<Result<HandshakeDto[]>> {
    try {
      const response = await this.repository.getHandshakePartners(userId);
      return { data: response?.data?.handshakes ?? [] };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  /** What this player says the game was. Empty rubs out the one on record. */
  async setHandshakeNote(handshakeId: string, note: string): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.setHandshakeNote(handshakeId, note);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  /** Unique needs a name. Not unique is final. */
  async setStandInVerdict(handshakeId: string, verdict: StandInVerdict, name?: string): Promise<Result<HandshakeDto>> {
    try {
      const response = await this.repository.setStandInVerdict(handshakeId, verdict, name);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }
}
